import { useEffect, useState } from "react";
import Box from "elements/Box";
import {
  Button,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Switch,
  FormControlLabel,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Chip,
  Alert,
  AlertTitle,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import { useSnackbar } from "notistack";
import { ApiRequest } from "GlobalFunctions";
import SyncIcon from "@mui/icons-material/Sync";
import EditIcon from "@mui/icons-material/Edit";

export default function DianResolutions(props: any) {
  const { enqueueSnackbar } = useSnackbar();
  const [config, setConfig] = useState<any>(null);
  const [resolutions, setResolutions] = useState<any[]>([]);
  const [syncing, setSyncing] = useState(false);
  const [resolutionDialog, setResolutionDialog] = useState(false);
  const [editingResolution, setEditingResolution] = useState<any>(null);

  // Numbering state
  const [numbering, setNumbering] = useState<any[]>([]);
  const [ncPrefix, setNcPrefix] = useState('');
  const [ncNumber, setNcNumber] = useState(0);
  const [ndPrefix, setNdPrefix] = useState('');
  const [ndNumber, setNdNumber] = useState(0);

  useEffect(() => {
    ApiRequest({
      url: "/dian/config/get",
      method: "get",
      headers: { ltkn: localStorage.getItem("ltkn") },
      setResponse: (response: any) => {
        if (response.config) setConfig(response.config);
      },
    });
    loadNumbering();
  }, []);

  const loadNumbering = () => {
    ApiRequest({
      url: "/dian/numbering/get",
      method: "get",
      headers: { ltkn: localStorage.getItem("ltkn") },
      setResponse: (response: any) => {
        if (response.data) {
          setNumbering(response.data);
          const nc = response.data.find((n: any) => n.document_type === 'credit_note');
          const nd = response.data.find((n: any) => n.document_type === 'debit_note');
          if (nc) { setNcPrefix(nc.prefix); setNcNumber(nc.current_number); }
          if (nd) { setNdPrefix(nd.prefix); setNdNumber(nd.current_number); }
        }
      },
    });
  };

  const saveNumberingItem = (document_type: string, prefix: string, current_number: number) => {
    ApiRequest({
      url: "/dian/numbering/save",
      method: "post",
      headers: { ltkn: localStorage.getItem("ltkn") },
      body: { document_type, prefix, current_number },
      setResponse: (r: any) => {
        if (r.status === 'ok') {
          enqueueSnackbar('Numeración guardada', { variant: 'success' });
          loadNumbering();
        } else {
          enqueueSnackbar(r.error || 'Error al guardar', { variant: 'error' });
        }
      },
    });
  };

  useEffect(() => {
    if (config) loadResolutions();
  }, [config]);

  const loadResolutions = () => {
    ApiRequest({
      url: "/dian/resolutions/list",
      method: "get",
      headers: { ltkn: localStorage.getItem("ltkn") },
      setResponse: (response: any) => {
        if (response.list) setResolutions(response.list.filter((r: any) => r.environment === config.environment));
      },
    });
  };

  const syncResolutions = () => {
    setSyncing(true);
    ApiRequest({
      url: "/dian/resolutions/sync",
      method: "post",
      headers: { ltkn: localStorage.getItem("ltkn") },
      body: { environment: config.environment },
      setResponse: (r: any) => {
        setSyncing(false);
        if (r.status === "ok") {
          enqueueSnackbar(`Sincronizadas ${r.total} resolución(es). Nuevas: ${r.inserted}, actualizadas: ${r.updated}, desactivadas: ${r.deactivated}.`, { variant: "success" });
          loadResolutions();
        } else {
          enqueueSnackbar(r.error || "Error al sincronizar resoluciones", { variant: "error" });
        }
      },
    });
  };

  const openEditResolution = (r: any) => {
    setEditingResolution({
      ...r,
      resolution_date: r.resolution_date ? String(r.resolution_date).slice(0, 10) : "",
      valid_date_from: r.valid_date_from ? String(r.valid_date_from).slice(0, 10) : "",
      valid_date_to: r.valid_date_to ? String(r.valid_date_to).slice(0, 10) : "",
      is_active: !!r.is_active,
    });
    setResolutionDialog(true);
  };

  const saveEditedResolution = () => {
    if (!editingResolution) return;
    ApiRequest({
      url: "/dian/resolutions/save",
      method: "post",
      headers: { ltkn: localStorage.getItem("ltkn") },
      body: editingResolution,
      setResponse: (r: any) => {
        if (r.status === "ok") {
          enqueueSnackbar("Resolución guardada", { variant: "success" });
          setResolutionDialog(false);
          setEditingResolution(null);
          loadResolutions();
        } else {
          enqueueSnackbar(r.error || "Error al guardar", { variant: "error" });
        }
      },
    });
  };

  if (!config) return null;

  return (
    <>
      <Box id="dianResolutions" className="box100" title="Resoluciones de Numeración (Producción)">
        <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 12 }}>
          <Button variant="outlined" startIcon={<SyncIcon />} onClick={syncResolutions} disabled={syncing || !config.has_certificate || !config.software_id} size="small">
            {syncing ? "Sincronizando…" : "Sincronizar desde DIAN"}
          </Button>
        </div>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Resolución</TableCell>
              <TableCell>Prefijo</TableCell>
              <TableCell>Tipo</TableCell>
              <TableCell>Rango</TableCell>
              <TableCell>Actual</TableCell>
              <TableCell>Vigencia</TableCell>
              <TableCell>Estado</TableCell>
              <TableCell align="right">Acciones</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {resolutions.map((r: any) => (
              <TableRow key={r.resolution_id}>
                <TableCell>{r.resolution_number}</TableCell>
                <TableCell><Chip label={r.prefix} size="small" /></TableCell>
                <TableCell>{r.document_type === "invoice" ? "Factura" : r.document_type === "credit_note" ? "Nota Crédito" : "Nota Débito"}</TableCell>
                <TableCell>{r.range_from} - {r.range_to}</TableCell>
                <TableCell>{r.current_number}</TableCell>
                <TableCell style={{ fontSize: 12 }}>
                  {r.valid_date_from && r.valid_date_to ? `${String(r.valid_date_from).slice(0, 10)} → ${String(r.valid_date_to).slice(0, 10)}` : "—"}
                </TableCell>
                <TableCell><Chip label={r.is_active ? "Activa" : "Inactiva"} color={r.is_active ? "success" : "default"} size="small" /></TableCell>
                <TableCell align="right">
                  <Tooltip title="Editar resolución">
                    <IconButton size="small" onClick={() => openEditResolution(r)}><EditIcon fontSize="small" /></IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
            {resolutions.length === 0 && (
              <TableRow>
                <TableCell colSpan={8} align="center" style={{ color: "#999" }}>
                  No hay resoluciones. Pulsa "Sincronizar desde DIAN".
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </Box>

      {/* Diálogo de edición */}
      <Dialog open={resolutionDialog} onClose={() => setResolutionDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>Editar Resolución</DialogTitle>
        <DialogContent>
          {editingResolution && (
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginTop: 8 }}>
              <TextField label="No. Resolución" value={editingResolution.resolution_number || ""} onChange={(e) => setEditingResolution({ ...editingResolution, resolution_number: e.target.value })} size="small" fullWidth />
              <TextField label="Fecha Resolución" type="date" value={editingResolution.resolution_date || ""} onChange={(e) => setEditingResolution({ ...editingResolution, resolution_date: e.target.value })} size="small" fullWidth InputLabelProps={{ shrink: true }} />
              <TextField label="Vigencia desde" type="date" value={editingResolution.valid_date_from || ""} onChange={(e) => setEditingResolution({ ...editingResolution, valid_date_from: e.target.value })} size="small" fullWidth InputLabelProps={{ shrink: true }} />
              <TextField label="Vigencia hasta" type="date" value={editingResolution.valid_date_to || ""} onChange={(e) => setEditingResolution({ ...editingResolution, valid_date_to: e.target.value })} size="small" fullWidth InputLabelProps={{ shrink: true }} />
              <TextField label="Prefijo" value={editingResolution.prefix || ""} onChange={(e) => setEditingResolution({ ...editingResolution, prefix: e.target.value })} size="small" fullWidth />
              <FormControl size="small" fullWidth>
                <InputLabel>Tipo de Documento</InputLabel>
                <Select value={editingResolution.document_type || "invoice"} label="Tipo de Documento" onChange={(e) => setEditingResolution({ ...editingResolution, document_type: e.target.value })}>
                  <MenuItem value="invoice">Factura</MenuItem>
                  <MenuItem value="credit_note">Nota Crédito</MenuItem>
                  <MenuItem value="debit_note">Nota Débito</MenuItem>
                </Select>
              </FormControl>
              <TextField label="Rango Desde" type="number" value={editingResolution.range_from || ""} onChange={(e) => setEditingResolution({ ...editingResolution, range_from: parseInt(e.target.value) || 0 })} size="small" fullWidth />
              <TextField label="Rango Hasta" type="number" value={editingResolution.range_to || ""} onChange={(e) => setEditingResolution({ ...editingResolution, range_to: parseInt(e.target.value) || 0 })} size="small" fullWidth />
              <TextField label="Número Actual" type="number" value={editingResolution.current_number || ""} onChange={(e) => setEditingResolution({ ...editingResolution, current_number: parseInt(e.target.value) || 0 })} size="small" fullWidth />
              <TextField label="Clave Técnica" value={editingResolution.technical_key || ""} onChange={(e) => setEditingResolution({ ...editingResolution, technical_key: e.target.value })} size="small" fullWidth />
              <FormControlLabel control={<Switch checked={!!editingResolution.is_active} onChange={(e) => setEditingResolution({ ...editingResolution, is_active: e.target.checked })} />} label="Activa" />
            </div>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setResolutionDialog(false); setEditingResolution(null); }}>Cancelar</Button>
          <Button variant="contained" onClick={saveEditedResolution}>Guardar</Button>
        </DialogActions>
      </Dialog>

      {/* Numeraciones NC/ND */}
      <Box id="dianNumbering" className="box100" title="Numeración Notas Crédito / Débito">
        <Alert severity="info" sx={{ mb: 2 }}>
          Las notas crédito y débito no requieren resolución DIAN. Configure aquí el prefijo y número desde el cual empezar.
        </Alert>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr auto", gap: 16, alignItems: "center", marginBottom: 16 }}>
          <TextField label="Prefijo NC" value={ncPrefix} onChange={(e) => setNcPrefix(e.target.value)} size="small" />
          <TextField label="Número actual NC" type="number" value={ncNumber} onChange={(e) => setNcNumber(parseInt(e.target.value) || 0)} size="small" />
          <Button variant="contained" size="small" onClick={() => saveNumberingItem('credit_note', ncPrefix, ncNumber)}>Guardar</Button>
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr auto", gap: 16, alignItems: "center" }}>
          <TextField label="Prefijo ND" value={ndPrefix} onChange={(e) => setNdPrefix(e.target.value)} size="small" />
          <TextField label="Número actual ND" type="number" value={ndNumber} onChange={(e) => setNdNumber(parseInt(e.target.value) || 0)} size="small" />
          <Button variant="contained" size="small" onClick={() => saveNumberingItem('debit_note', ndPrefix, ndNumber)}>Guardar</Button>
        </div>
      </Box>
    </>
  );
}
